import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useUser } from '../../../../contexts/UserContext';
import { toast } from 'react-toastify';

import { useRingCentralCall } from '../../../../hooks/useRingCentralCall';
import {
  setAudioElements,
  switchInCall,
  ensureWebPhone,
  resetWebPhone,
} from '../../../../lib/ringcentral/webphone';
import {
  hangupActiveCall,
  onSessionEvent,
  offSessionEvent,
  isLocalAudioMuted,
  muteLocalAudio,
  unmuteLocalAudio,
  transferActiveCall,
  warmTransferActiveCall,
  returnToClient,
} from '../../../../lib/ringcentral/session';
import {
  ensureAuthorized,
  getAccessToken,
  getStoredTokens,
  openAuthorizePopup,
} from '../../../../lib/ringcentral/auth';
import { RC_SERVER_URL } from '../../../../lib/ringcentral/config';
import {
  useCompleteStep,
  useDiscardCallAttempt,
  useMarkContactMade,
  useObserveContact,
  useCallStarted,
  useCallEnded,
  useLogFollowupCall,
  useLogOutsideCadenceCall,
  useSetRunOutcome,
  useRecordHotSeat,
} from '../../../../services/onboardingBoard';

import CallCommandCenter from './CallCommandCenter';
import PickupCard from './PickupCard';
import {
  COPY as PICKUP_COPY,
  PICKUP_CLAIM_MS,
  PICKUP_DEADLINE_MS,
  PICKUP_ENABLED,
  PICKUP_POLL_MS,
  SWITCH_CEILING_MS,
  activeCallFor,
  isConnectedRow,
  nextSwitchAttempt,
  replacesHeader,
  shouldOfferPickup,
} from './pickup';
import CallDock from './CallDock';
import SignupFlyover from '../signup/SignupFlyover';
import { isOwnedBy, notYoursMessage } from '../ownership';

// The Call Command Center provider (Nic 2026-08-12): ONE app-level owner of
// the board's live call, mounted in MainRouter so the call survives any
// navigation — the webphone + session layer is already module-global; this
// provider is the persistent UI handle on top of it.
//
// It deliberately does NOT touch RcCallModal: every other calling surface in
// the app (Leads, Event Leads, KPI dashboard, Pending Clients, claims
// SideBar) keeps the shared dialer untouched. The board alone routes its
// step calls here.
//
// The call is a stepped flow that mirrors reality:
//   idle -> connecting -> talking --(Made contact)--> contact_made
//   call ends: declared outcome -> clean close (no prompt)
//              contact made, nothing declared -> soft "next move" wrap-up
//              nothing declared at all -> the classic outcome question
// The ordering guarantee lives here: Send Link / Transfer / Booked only
// exist AFTER Made contact.

const CallCenterContext = createContext(null);

export const useCallCenter = () => {
  const ctx = useContext(CallCenterContext);
  return (
    ctx || {
      active: null,
      dockVisible: false,
      startStepCall: () => {},
      openSignup: () => {},
      signupOpen: false,
      testCall: null,
      startTestCall: () => {},
      endTestCall: () => {},
      clearTestCall: () => {},
      busy: false,
    }
  );
};

// A warm consult rings this long before it counts as not picked up (inside
// RingCentral's default ring-to-voicemail, so "no answer" is never "their
// voicemail answered").
const CONSULT_RING_MS = 20 * 1000;

export const CallCenterProvider = ({ user, children }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Client Onboarding calls (Nic 2026-10-10): the same webphone, dock and
  // navigation-proof session as Sales, for a signed client. Their Client
  // Workspace carries the live pill while it is open (it says so here); any
  // other time the call shows as the dock, and expanding it reopens them.
  const [clientPanel, setClientPanel] = useState(null); // onboardingId
  const { startCallTo } = useRingCentralCall();

  const localAudioRef = useRef(null);
  const remoteAudioRef = useRef(null);

  // The one active call. Null when idle. boardContext is snapshotted at call
  // start (transfer targets, scheduler link, scripts, viewer id) so the
  // center works from any page without a board fetch.
  const [call, setCall] = useState(null);
  // {run, step, lead, boardContext, phase, placedAt, rcCallSessionId,
  //  callLive, contactMade, leadOutreachId, declaredOutcome, linkSent,
  //  minimized, transferStaged: {target, mode} | null, failed}
  const callRef = useRef(null);
  callRef.current = call;

  // Test Audio (settings menu): a self-test call to the staffer's OWN phone.
  // Reuses the same audio elements + single-session machinery as real calls,
  // but carries no run/lead and logs nothing app-side.
  // { number, status: 'dialing' | 'live' | 'ended' | 'failed' }
  const [testCall, setTestCall] = useState(null);
  const testCallRef = useRef(null);
  testCallRef.current = testCall;

  const [muted, setMuted] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);

  // Pick up a RingCentral call in the app (Nic 2026-11-02): a call this
  // person is on OUTSIDE the browser (answered or placed in the RingCentral
  // app), as the server sees it. { call, match, phase, note } | null; the
  // phases are offer, switching, routing (switched, waiting for the Sales
  // board to open the Workspace), online (switched, someone else's person:
  // mute, hang up, one hot transfer). Their own lead or client becomes a
  // normal `call` instead, through startFollowUpCall / startOutsideCadence
  // / adoptClientCall with `adopted`.
  const [pickup, setPickup] = useState(null);
  const pickupRef = useRef(null);
  pickupRef.current = pickup;
  // The switched call waiting for the Sales board to claim it.
  const [pendingPickup, setPendingPickup] = useState(null);
  const pendingPickupRef = useRef(null);
  pendingPickupRef.current = pendingPickup;
  // A picked-up call that lives only on the card (someone else's person, or
  // the switch in flight) is a live call too: no other call may start over
  // it and the idle self-heal must not rebuild the phone under it.
  const pickupLive = () =>
    ['switching', 'routing', 'online'].includes(pickupRef.current?.phase);
  // The server said this person may not pick up calls: the poll stops.
  const deniedRef = useRef(false);
  // And it never starts for someone who cannot work leads or clients (a
  // viewer with a RingCentral login would only collect a 403 toast).
  const { hasAnyPermission } = useUser();
  const canPickUp = hasAnyPermission(['onboarding_board:work', 'client_onboarding:work']);
  // The pick-up attempt in flight: Cancel or its deadline forgets it, and
  // every step after an await checks it is still the one.
  const pickupAttemptRef = useRef(null);

  // The signup flyover (Nic 2026-09-22): { leadId, leadName } | null. Lives
  // here, app-level, so it rides along with the live call; while it is open
  // the Command Center docks and the flyover header carries the call bar.
  const [signup, setSignup] = useState(null);
  const signupRef = useRef(null);
  signupRef.current = signup;

  const invalidateBoard = useCallback(() => {
    queryClient.invalidateQueries(['onboarding-board']);
    queryClient.invalidateQueries(['onboarding-metrics']);
    // Call-center actions create RC records; the outside-activity query is
    // keyed outside the board prefix (so the SSE stream can't hammer RC)
    // and refreshes only on deliberate actions like these.
    queryClient.invalidateQueries(['rc-activity']);
  }, [queryClient]);

  const onMutationError = (err) =>
    toast.error(err?.response?.data?.msg || 'That didn’t go through.');

  // Ownership rule (Nic 2026-09-29): calls dial from THIS browser, so this is
  // the gate that actually stops a dial on someone else's lead. Refuses (and
  // says why) before anything opens; the server 403s the logging anyway.
  const userIdRef = useRef(user?.userId || null);
  userIdRef.current = user?.userId || null;
  const refuseIfNotOwner = useCallback((run, boardContext) => {
    if (!run?.ownerUserId) return false;
    const viewerId = boardContext?.viewerUserId || userIdRef.current;
    if (isOwnedBy(run.ownerUserId, viewerId)) return false;
    toast.error(notYoursMessage(run.ownerName));
    return true;
  }, []);

  const completeStep = useCompleteStep(null, { onError: onMutationError });
  const discardCall = useDiscardCallAttempt(null, { onError: onMutationError });
  const markContactMade = useMarkContactMade(null, {
    onError: onMutationError,
  });
  const observeContact = useObserveContact(invalidateBoard, {
    onError: () => {},
  });
  const setRunOutcome = useSetRunOutcome(null, { onError: onMutationError });
  // Silent onError on purpose: settleTransfer's own catch shows the
  // tailored "went through, but recording it failed" retry message — a
  // second generic toast from here doubled every failure (Nic 2026-08-16).
  const recordHotSeat = useRecordHotSeat({ onError: () => {} });
  const callStartedMut = useCallStarted(null, { onError: () => {} });
  const callEndedMut = useCallEnded(null, { onError: () => {} });
  // Follow-up calls have no step; this records the touch itself.
  // The call still goes out when logging fails, but the rep hears about it
  // (QA 2026-10-26: a 409 here used to vanish, so nothing was on the card).
  const logCallFailed = (err) =>
    toast.error(
      err?.response?.data?.msg ||
        "This call wasn't logged on their card. Note it after you hang up.",
    );
  const logFollowupCallMut = useLogFollowupCall(null, {
    onError: logCallFailed,
  });
  // Outside-of-cadence calls: touch record + the per-lead usage counter.
  const logOutsideCadenceCallMut = useLogOutsideCadenceCall(null, {
    onError: logCallFailed,
  });

  const busy =
    completeStep.isLoading ||
    discardCall.isLoading ||
    markContactMade.isLoading ||
    setRunOutcome.isLoading;

  const patchCall = useCallback((patch) => {
    setCall((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  // ---- Session lifecycle ---------------------------------------------------

  // One terminated handler for the provider's lifetime; it reads the live
  // call via ref so re-subscribes are never needed mid-call.
  useEffect(() => {
    const handleTerminated = () => {
      const current = callRef.current;
      if (!current || !current.callLive) return;
      setMuted(false);
      // The client hung up while on hold for a warm consult (2026-10-29):
      // the consult leg ends too, and the rep is told.
      if (current.consult) {
        consultTokenRef.current = null;
        Promise.resolve(returnToClient({ wasMuted: true })).catch(() => {});
        if (current.consult.state !== 'completing') {
          toast.info(`${current.lead?.leadFirstName || 'The veteran'} hung up while on hold.`);
        }
      }
      // The call is over: nothing left to recover after a refresh.
      try {
        sessionStorage.removeItem('cc_recovery_sip');
        sessionStorage.removeItem('cc_recovery_ctx');
      } catch (_) {}
      if (current.run?.cadenceRunId) {
        callEndedMut.mutate({ cadenceRunId: current.run.cadenceRunId });
      }
      setCall((prev) => {
        if (!prev) return prev;
        // A Client Onboarding call has no wrap-up: it just ends.
        if (prev.client) return null;
        // THE PANEL NEVER DISAPPEARS ON A HANGUP (Nic 2026-08-15). This used
        // to `return null` whenever an outcome was already declared, to skip
        // a post-call question that would be moot (and 409). But the person
        // is on a PHONE, and back when a mid-call booking auto-declared
        // 'booked', the moment the client hung up the whole panel evaporated
        // with no "Call ended" and no explanation — "my call disappeared".
        //
        // A transfer has its own settled confirmation view, and every other
        // outcome now lands on the wrap-up, so nothing needs deleting to
        // avoid re-asking. Dismissal is a human decision.
        // ...except while the signup flyover is open: the rep is mid-signup
        // and the flyover's own bar already reads "Call ended". Closing the
        // flyover brings the wrap-up back.
        return {
          ...prev,
          callLive: false,
          // A signature that lands just after the hangup still counts as
          // signed on the call (the hot seat, Nic 2026-10-05).
          callEndedAt: Date.now(),
          minimized: !!signupRef.current,
        };
      });
      invalidateBoard();
    };
    onSessionEvent('terminated', handleTerminated);
    return () => {
      offSessionEvent('terminated', handleTerminated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Test-call end detection: its own listener on the same bus, so either
  // side hanging up (or a ring-out) returns the modal to its input state.
  // The real-call handler above is untouched — its callLive guard holds
  // because `call` stays null for the whole life of a test call, and vice
  // versa.
  useEffect(() => {
    const onTestTerminated = () => {
      const t = testCallRef.current;
      if (!t || (t.status !== 'live' && t.status !== 'dialing')) return;
      setTestCall((prev) => (prev ? { ...prev, status: 'ended' } : prev));
    };
    onSessionEvent('terminated', onTestTerminated);
    return () => {
      offSessionEvent('terminated', onTestTerminated);
    };
  }, []);

  // A test call must never outlive the page. Real calls survive a refresh by
  // design (orphan banner + take back control); a test call has nothing to
  // recover, and the RC app lacks Call Control so the page could not end an
  // orphaned one anyway (DELETE returns 403). Hang up DURING the unload —
  // pagehide still has the live SIP socket, so the BYE usually flushes. The
  // sessionStorage flag lets the post-refresh orphan check name a straggler
  // honestly if the BYE ever loses the race.
  useEffect(() => {
    const active =
      testCall?.status === 'live' || testCall?.status === 'dialing';
    if (!active) {
      try {
        sessionStorage.removeItem('cc_test_call_live');
      } catch (_) {}
      return undefined;
    }
    try {
      sessionStorage.setItem('cc_test_call_live', '1');
    } catch (_) {}
    const onPageHide = () => {
      try {
        hangupActiveCall();
      } catch (_) {}
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [testCall?.status]);

  // Stale-registration self-heal, layer 1 of 2 (Kit's overnight ghost call):
  // a tab hidden for a long stretch (overnight, lunch, laptop sleep) comes
  // back holding a SIP registration that quietly died — the first call then
  // dials into the void with a live-looking UI. On refocus after a long
  // hide, silently discard the cached webphone so the next call provisions
  // fresh. Never mid-call; costs ~a second on the next dial; no UI. Layer 2
  // is the socket health check in ensureWebPhone (webphone.js), which
  // catches deaths that happen while the tab stays visible.
  //
  // 4 MINUTES, not longer: the SDK renews its RC registration every ~57s on
  // a timer, and Chrome throttles hidden-tab timers after ~5 min — so a tab
  // hidden 5+ min can come back with a LAPSED registration on a still-OPEN
  // socket, which layer 2's wire check cannot see (the SDK exposes no
  // "is my registration valid" signal). Resetting on any hide >= 4 min
  // covers that band; shorter hides never throttled, so the lease held.
  const hiddenAtRef = useRef(null);
  useEffect(() => {
    const STALE_AFTER_MS = 4 * 60 * 1000;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenFor = hiddenAtRef.current
        ? Date.now() - hiddenAtRef.current
        : 0;
      hiddenAtRef.current = null;
      if (hiddenFor < STALE_AFTER_MS) return;
      if (
        callRef.current ||
        pickupLive() ||
        testCallRef.current?.status === 'live' ||
        testCallRef.current?.status === 'dialing'
      ) {
        return;
      }
      resetWebPhone();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Call timer.
  useEffect(() => {
    if (!call?.callLive) {
      setElapsedMs(0);
      return undefined;
    }
    const t = setInterval(() => setElapsedMs((v) => v + 1000), 1000);
    return () => clearInterval(t);
  }, [call?.callLive]);

  // Refresh guard (work item C): an accidental refresh mid-call would orphan
  // the browser leg — warn first.
  useEffect(() => {
    if (!call?.callLive) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [call?.callLive]);

  // Keep the screen awake while a call is live (lifted from RcCallModal).
  useEffect(() => {
    if (!call?.callLive) return undefined;
    let lock = null;
    let disposed = false;
    const acquire = async () => {
      try {
        lock = await navigator.wakeLock?.request?.('screen');
      } catch (_) {}
    };
    const onVisibility = () => {
      if (!disposed && document.visibilityState === 'visible') acquire();
    };
    acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      try {
        lock?.release?.();
      } catch (_) {}
    };
  }, [call?.callLive]);

  // ---- Recovery snapshot ---------------------------------------------------
  // While the call is live, keep a slim restorable copy of the center's
  // state in sessionStorage (scripts excluded — too heavy, the wing degrades
  // gracefully). Together with cc_recovery_sip this makes "take back
  // control" a full resume after a refresh.
  useEffect(() => {
    if (!call?.callLive) return;
    try {
      const { boardContext, ...rest } = call;
      sessionStorage.setItem(
        'cc_recovery_ctx',
        JSON.stringify({
          ...rest,
          consult: null,
          boardContext: {
            transferTargets: boardContext?.transferTargets || [],
            viewerUserId: boardContext?.viewerUserId || null,
            orgCalendlyUrl: boardContext?.orgCalendlyUrl || '',
            callGuide: boardContext?.callGuide || null,
            scripts: {},
          },
        }),
      );
    } catch (_) {}
  }, [call]);

  // ---- Mid-call booking detection ------------------------------------------
  // After contact is made, the specialist may book for the lead in a Calendly
  // tab (prefilled link) — the webhook writes the row within seconds. This
  // poll is purely INFORMATIONAL now (Nic 2026-08-15): it greys the Calendly
  // disc to "Booked!" so whoever is on the phone can see it landed. It used
  // to feed an auto-confirm; confirming is the human's job from the tile.
  useEffect(() => {
    // `placed` matters: startFollowUpCall opens with contactMade TRUE and
    // callLive FALSE, and this effect's first check() runs immediately, so
    // without it the panel would query before anyone had dialled.
    const shouldPoll =
      !!call &&
      !call.client &&
      call.placed &&
      call.contactMade &&
      !call.declaredOutcome &&
      !call.liveBooking;
    if (!shouldPoll) return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        const res = await axios.get(
          `/onboarding-board/leads/${call.lead.leadId}/live-booking`,
        );
        if (!cancelled && res.data?.booking) {
          patchCall({ liveBooking: res.data.booking });
        }
      } catch (_) {
        /* best effort */
      }
    };
    check();
    const t = setInterval(check, 10000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    call?.placed,
    call?.contactMade,
    call?.declaredOutcome,
    call?.liveBooking,
    call?.lead?.leadId,
  ]);

  // ---- Orphan-call recovery (work item C) ----------------------------------
  // A refresh mid-call destroys the browser's session handle while the RC
  // call can live on (phone legs connected). On mount: if we hold RC tokens
  // and RC reports an in-progress call for this extension while we have no
  // local session, offer a deliberate hang up. Best effort — any API
  // surprise just means no banner.
  const [orphanCall, setOrphanCall] = useState(null); // {telephonySessionId}

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        if (!getStoredTokens()?.access_token) return;
        if (callRef.current) return;
        const authorized = await ensureAuthorized();
        if (!authorized || cancelled) return;
        const res = await fetch(
          `${RC_SERVER_URL}/restapi/v1.0/account/~/extension/~/active-calls?view=Simple&perPage=10`,
          { headers: { Authorization: `Bearer ${getAccessToken()}` } },
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const live = (data.records || []).find(
          (r) => r.result === 'In Progress' && r.telephonySessionId,
        );
        // "Keep it" is remembered per session id (sessionStorage) so the
        // banner never nags again for a call the user chose to keep — dev
        // hot reloads and prod re-navigations both respect the dismissal.
        const dismissed = sessionStorage.getItem('cc_orphan_dismissed');
        // Was the pre-refresh call a TEST call? The pagehide hangup should
        // have ended it, but if the BYE lost the unload race the orphan is
        // a headset test, not a lead call - the banner says so. Read-and-
        // clear: the flag only describes the call from THIS refresh.
        let wasTestCall = false;
        try {
          wasTestCall = sessionStorage.getItem('cc_test_call_live') === '1';
          sessionStorage.removeItem('cc_test_call_live');
        } catch (_) {}
        if (
          live &&
          live.telephonySessionId !== dismissed &&
          !callRef.current &&
          !cancelled
        ) {
          // Recoverable when WE placed this exact call before the refresh:
          // the SIP snapshot + center context make "take back control"
          // possible (call switch via a Replaces INVITE).
          let recovery = null;
          try {
            const sip = JSON.parse(
              sessionStorage.getItem('cc_recovery_sip') || 'null',
            );
            const ctx = JSON.parse(
              sessionStorage.getItem('cc_recovery_ctx') || 'null',
            );
            if (
              sip &&
              ctx &&
              sip.telephonySessionId === live.telephonySessionId
            ) {
              recovery = { sip, ctx };
            }
          } catch (_) {}
          // Only a call THIS browser dialed and lost (a recovery snapshot,
          // or this tab's own test call). RingCentral lists every call on
          // the line, desk phone and app included, and keeps a finished one
          // listed for a couple of minutes, so the banner fired after
          // nearly every call (Nic 2026-11-02). Calls on other devices are
          // the pick-up card's business.
          if (!recovery && !wasTestCall) return;
          setOrphanCall({
            telephonySessionId: live.telephonySessionId,
            recovery,
            // A real call's recovery snapshot outranks the flag (a stale
            // flag can never relabel a recoverable lead call).
            isTest: wasTestCall && !recovery,
          });
        }
      } catch (_) {
        /* best effort */
      }
    };
    const t = setTimeout(check, 2500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Take back control": pull the refresh-orphaned call into a fresh WebRTC
  // session (SIP Replaces — the official apps' device-switch mechanism) and
  // restore the command center exactly where it was.
  const takeBackControl = useCallback(async () => {
    const orphan = orphanCall;
    if (!orphan?.recovery) return;
    setOrphanCall(null);
    try {
      // Mic permission first (same early-ask as a fresh call).
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        stream.getTracks().forEach((t) => t.stop());
      } catch (micErr) {
        toast.error('Microphone permission is required to take the call back.');
        setOrphanCall(orphan);
        return;
      }
      const authorized = await ensureAuthorized();
      if (!authorized) {
        toast.error(
          'Log into RingCentral first (the chip in the board header).',
        );
        setOrphanCall(orphan);
        return;
      }
      const webPhone = await ensureWebPhone(getAccessToken());
      setAudioElements(remoteAudioRef.current, localAudioRef.current);
      await switchInCall(webPhone, orphan.recovery.sip);

      const ctx = orphan.recovery.ctx;
      const elapsed = ctx.placedAt
        ? Math.max(0, Date.now() - new Date(ctx.placedAt).getTime())
        : 0;
      setElapsedMs(elapsed);
      setCall({
        ...ctx,
        callLive: true,
        dialing: false,
        minimized: false,
        failed: false,
        transferStaged: null,
        consult: null,
      });
      toast.success('Call is back in your hands.');
    } catch (e) {
      toast.error(
        'Could not take the call back. It stays connected on your phone.',
      );
      setOrphanCall(orphan);
    }
  }, [orphanCall]);

  const hangupOrphan = useCallback(async () => {
    const orphan = orphanCall;
    setOrphanCall(null);
    if (!orphan) return;
    try {
      const res = await fetch(
        `${RC_SERVER_URL}/restapi/v1.0/account/~/telephony/sessions/${orphan.telephonySessionId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        },
      );
      if (res.ok || res.status === 404) {
        toast.success('The call was ended.');
      } else if (res.status === 403) {
        // The RC app lacks the Call Control permission — the browser cannot
        // end a call it no longer holds a WebRTC handle to.
        toast.error(
          'RingCentral would not let this page end the call (the app lacks Call Control). Hang up from your phone or the RingCentral app.',
          { autoClose: 9000 },
        );
      } else {
        toast.error('Could not end the call from here. End it in RingCentral.');
      }
    } catch (_) {
      toast.error('Could not end the call from here. End it in RingCentral.');
    }
  }, [orphanCall]);

  // ---- Pick up a RingCentral call (Nic 2026-11-02, backlog 136) ------------
  // While idle and visible, ask the server every PICKUP_POLL_MS whether this
  // person is on a call outside the browser and who is on it. The server
  // reads its own presence spans (one cheap query, usually empty), so this
  // costs nothing most of the day.
  useEffect(() => {
    if (!PICKUP_ENABLED || !canPickUp) return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        if (document.visibilityState !== 'visible') return;
        if (!getStoredTokens()?.access_token) return;
        if (callRef.current) return;
        const t = testCallRef.current?.status;
        if (t === 'live' || t === 'dialing') return;
        if (deniedRef.current) return;
        const current = pickupRef.current;
        if (current && current.phase !== 'offer') return;
        let res;
        try {
          res = await axios.get('/presence/my-call');
        } catch (e) {
          // Not someone who works leads or clients: stop asking.
          if (e?.response?.status === 403) deniedRef.current = true;
          return;
        }
        if (cancelled) return;
        const { call: live, match } = res.data || {};
        // An outbound call the server cannot confirm: ask RingCentral
        // whether the far end is on the line before offering it.
        if (live && match && !live.confirmed) {
          let connected = false;
          try {
            if (await ensureAuthorized()) {
              const pres = await fetch(
                `${RC_SERVER_URL}/restapi/v1.0/account/~/extension/~/presence?detailedTelephonyState=true`,
                { headers: { Authorization: `Bearer ${getAccessToken()}` } },
              );
              if (pres.ok) connected = isConnectedRow(activeCallFor(await pres.json(), live.sessionId));
              else if ((pres.status === 401 || pres.status === 403) && !sessionStorage.getItem('cc_pickup_relogin')) {
                // Once per tab: the token predates Read Presence.
                sessionStorage.setItem('cc_pickup_relogin', '1');
                toast.warn(PICKUP_COPY.relogin, { autoClose: 12000 });
              }
            }
          } catch (_) {}
          if (cancelled) return;
          if (!connected) {
            setPickup((prev) => (prev && prev.phase === 'offer' ? null : prev));
            return;
          }
        }
        let ownSip = null;
        let dismissedId = null;
        try {
          ownSip = JSON.parse(sessionStorage.getItem('cc_recovery_sip') || 'null');
          dismissedId = sessionStorage.getItem('cc_pickup_dismissed');
        } catch (_) {}
        const offer = shouldOfferPickup({
          call: live,
          match,
          ownSip,
          dismissedId,
          hasCall: !!callRef.current,
          testCall: testCallRef.current?.status || null,
          // The orphan banner only outranks the card for a call THIS browser
          // dialed and lost to a refresh (it has the recovery snapshot).
          orphan: !!orphanCall?.recovery,
        });
        setPickup((prev) => {
          if (!offer) return prev && prev.phase === 'offer' ? null : prev;
          if (prev && prev.phase !== 'offer') return prev;
          return { call: live, match, phase: 'offer', note: null };
        });
      } catch (_) {
        /* best effort */
      }
    };
    check();
    const id = setInterval(check, PICKUP_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orphanCall, canPickUp]);

  // The switched call ended while the card still held it (someone else's
  // person, or the board never claimed it): the card goes, with a word.
  useEffect(() => {
    const onEnded = () => {
      const current = pickupRef.current;
      if (!current || current.phase === 'offer' || current.handingOff) return;
      setPickup(null);
      setPendingPickup(null);
      setMuted(false);
      toast.info(PICKUP_COPY.ended);
    };
    onSessionEvent('terminated', onEnded);
    return () => {
      offSessionEvent('terminated', onEnded);
    };
  }, []);

  const dismissPickup = useCallback(() => {
    const current = pickupRef.current;
    try {
      if (current?.call?.sessionId) sessionStorage.setItem('cc_pickup_dismissed', current.call.sessionId);
    } catch (_) {}
    setPickup(null);
  }, []);

  // The one hand-off from the pick-up card: whoever claims it on the Sales
  // board gets the live-call facts and the card closes.
  const takePendingPickup = useCallback(() => {
    const adopted = pendingPickupRef.current;
    if (!adopted) return null;
    setPendingPickup(null);
    setPickup(null);
    return adopted;
  }, []);

  // Their own client (Onboarding): the startClientCall shape, already live.
  const adoptClientCall = useCallback(
    (client, adopted) => {
      if (!client?.onboardingId || !adopted) return;
      const next = {
        client,
        run: null,
        step: null,
        lead: {
          leadFirstName: client.firstName || '',
          leadLastName: client.lastName || '',
          leadPhone: client.phone || null,
        },
        boardContext: {},
        placed: true,
        dialing: false,
        placedAt: adopted.startedAt || null,
        rcCallSessionId: adopted.telephonySessionId || null,
        callLive: true,
        adopted: true,
        contactMade: false,
        leadOutreachId: null,
        declaredOutcome: null,
        linkSent: false,
        minimized: false,
        transferStaged: null,
        consult: null,
        failed: false,
      };
      callRef.current = next;
      setCall(next);
      setMuted(false);
      if (adopted.startedAt) {
        setElapsedMs(Math.max(0, Date.now() - new Date(adopted.startedAt).getTime()));
      }
      navigate(`/operations/client-onboarding?open=${client.onboardingId}`);
    },
    [navigate],
  );

  // Bring the call into this browser: the server confirms it is live and
  // logs it, the person's own presence read gives the SIP tags, the INVITE
  // with Replaces takes the audio (retried once with the tags swapped, or
  // without early-only on a 486), then their person opens, or the card
  // becomes the on-the-line view for someone else's.
  const pickUpCall = useCallback(async () => {
    const current = pickupRef.current;
    if (!current?.call?.sessionId || current.phase !== 'offer') return;
    const token = {};
    pickupAttemptRef.current = token;
    const abandoned = () => pickupAttemptRef.current !== token;
    // Every await before the INVITE goes through here: once Cancel or the
    // deadline forgot this attempt, nothing after it runs.
    const step = async (p) => {
      const v = await p;
      if (abandoned()) {
        const err = new Error('Pick-up abandoned');
        err.code = 'abandoned';
        throw err;
      }
      return v;
    };
    const quiet = (e) => e?.code === 'abandoned';
    const back = (phase, note = null, extra = {}) =>
      setPickup((prev) => (prev ? { ...prev, phase, note, ...extra } : prev));
    const giveUp = (msg) => {
      if (!abandoned()) pickupAttemptRef.current = null;
      toast.error(msg);
      setPickup((prev) => (prev ? { ...prev, phase: 'offer', note: null, canCancel: false } : prev));
    };
    const drop = (msg) => {
      if (!abandoned()) pickupAttemptRef.current = null;
      if (msg) toast.info(msg);
      setPickup(null);
    };
    // Everything before the INVITE (the adopt, the microphone prompt, the
    // presence read, the phone registration) gets one deadline and a
    // Cancel on the card; the switch itself has its own ceiling.
    let inviteSent = false;
    const deadline = setTimeout(() => {
      if (abandoned() || inviteSent) return;
      giveUp(PICKUP_COPY.slow);
    }, PICKUP_DEADLINE_MS);
    back('switching', null, { canCancel: true });
    let adopted;
    let sip;
    try {
      try {
        const res = await step(axios.post(`/presence/my-call/${encodeURIComponent(current.call.sessionId)}/adopt`));
        adopted = res.data;
      } catch (e) {
        if (quiet(e)) throw e;
        if (e?.response?.status === 410) drop(PICKUP_COPY.ended);
        else giveUp(e?.response?.data?.msg || PICKUP_COPY.failed);
        return;
      }
      try {
        const stream = await step(navigator.mediaDevices.getUserMedia({ audio: true }));
        stream.getTracks().forEach((t) => t.stop());
      } catch (e) {
        if (quiet(e)) throw e;
        giveUp(PICKUP_COPY.mic);
        return;
      }
      const authorized = await step(ensureAuthorized());
      if (!authorized) {
        giveUp(PICKUP_COPY.login);
        return;
      }
      let row = null;
      let denied = false;
      try {
        const res = await step(
          fetch(
            `${RC_SERVER_URL}/restapi/v1.0/account/~/extension/~/presence?detailedTelephonyState=true&sipData=true`,
            { headers: { Authorization: `Bearer ${getAccessToken()}` } },
          ),
        );
        if (res.ok) row = activeCallFor(await step(res.json()), current.call.sessionId);
        else if (res.status === 401 || res.status === 403) denied = true;
      } catch (e) {
        if (quiet(e)) throw e;
      }
      if (denied) {
        // A token minted before Read Presence was added to the app: the
        // person has to sign out of RingCentral here and back in once.
        toast.warn(PICKUP_COPY.relogin, { autoClose: 12000 });
        pickupAttemptRef.current = null;
        dismissPickup();
        return;
      }
      if (!row) {
        drop(PICKUP_COPY.ended);
        return;
      }
      if (!isConnectedRow(row)) {
        giveUp(PICKUP_COPY.notYet);
        return;
      }
      sip = {
        telephonySessionId: row.telephonySessionId || current.call.sessionId,
        toTag: row.sipData?.toTag || '',
        fromTag: row.sipData?.fromTag || '',
      };
      if (!sip.toTag || !sip.fromTag) {
        giveUp(PICKUP_COPY.failed);
        return;
      }
      // The phone itself, still under the deadline.
      await step(ensureWebPhone(getAccessToken()));
      inviteSent = true;
      back('switching', null, { canCancel: false });
    } catch (e) {
      if (!quiet(e)) giveUp(PICKUP_COPY.failed);
      return;
    } finally {
      clearTimeout(deadline);
    }
    let session = null;
    let late = null;
    const startedAt = Date.now();
    let attempt = { swapped: false, earlyOnly: true };
    for (let n = 1; n <= 3 && attempt; n += 1) {
      if (Date.now() - startedAt > SWITCH_CEILING_MS) break;
      let attemptPromise = null;
      try {
        const webPhone = await ensureWebPhone(getAccessToken());
        setAudioElements(remoteAudioRef.current, localAudioRef.current);
        // An INVITE RingCentral never answers must not hang the card: the
        // attempt races what is left of the ceiling. One that answers after
        // we gave up has still switched the call, and is kept (below).
        const left = Math.max(1000, SWITCH_CEILING_MS - (Date.now() - startedAt));
        let timer;
        attemptPromise = switchInCall(webPhone, {
          ...sip,
          callee: adopted.callee || 'whatever',
          replaces: replacesHeader(sip, attempt),
        });
        const timeout = new Promise((_, reject) => {
          timer = setTimeout(() => {
            const err = new Error('Switch timed out');
            err.code = 'switch_timeout';
            reject(err);
          }, left);
        });
        try {
          // eslint-disable-next-line no-await-in-loop
          session = await Promise.race([attemptPromise, timeout]);
        } finally {
          clearTimeout(timer);
        }
        console.log('[pickup] switched on attempt', n, attempt);
        break;
      } catch (e) {
        console.log('[pickup] switch refused', n, attempt, e?.subject || e?.message);
        if (e?.code === 'switch_timeout') {
          late = attemptPromise;
          break;
        }
        attempt = nextSwitchAttempt(e?.subject, n);
      }
    }
    pickupAttemptRef.current = null;
    // The call is in the browser: their person opens, or the card becomes
    // the on-the-line view for someone else's.
    const routeSwitched = (switched) => {
      // A refresh mid-call can take it back, like a dialed call.
      try {
        if (switched.sessionId && switched.localTag && switched.remoteTag) {
          sessionStorage.setItem(
            'cc_recovery_sip',
            JSON.stringify({ telephonySessionId: switched.sessionId, fromTag: switched.localTag, toTag: switched.remoteTag }),
          );
        }
      } catch (_) {}
      const live = {
        telephonySessionId: adopted.sessionId || current.call.sessionId,
        startedAt: adopted.startedAt || current.call.startedAt || null,
        direction: adopted.direction || current.call.direction || null,
      };
      const match = adopted.match || current.match;
      if (match?.open === 'sales' && match.cadenceRunId) {
        setPendingPickup({ ...live, cadenceRunId: match.cadenceRunId, runState: match.runState || null });
        back('routing');
        navigate(
          `/operations/onboarding-support?view=${match.runState === 'contact_made' ? 'clients' : 'contact'}&pickup=${encodeURIComponent(match.cadenceRunId)}`,
        );
        setTimeout(() => {
          if (!pendingPickupRef.current || pendingPickupRef.current.telephonySessionId !== live.telephonySessionId) return;
          setPendingPickup(null);
          setPickup((prev) => (prev && prev.phase === 'routing' ? { ...prev, phase: 'online', note: PICKUP_COPY.unclaimed } : prev));
        }, PICKUP_CLAIM_MS);
        return;
      }
      if (match?.open === 'onboarding' && match.onboardingId) {
        setPickup(null);
        adoptClientCall(
          {
            onboardingId: match.onboardingId,
            firstName: String(match.name || '').split(' ')[0] || '',
            lastName: String(match.name || '').split(' ').slice(1).join(' '),
            phone: adopted.callee || null,
          },
          live,
        );
        return;
      }
      back('online');
    };
    if (!session) {
      giveUp(PICKUP_COPY.failed);
      if (late) {
        // The INVITE was still out. If RingCentral answers it after all,
        // the switch HAS happened (the phone app's leg is gone), so the
        // call is kept and routed as if on time; hanging it up would drop
        // the person. Only another call started meanwhile cannot share
        // the line with it.
        Promise.resolve(late)
          .then((answered) => {
            if (!answered) return;
            if (callRef.current || pickupLive()) {
              try {
                answered.hangup?.();
              } catch (_) {}
              return;
            }
            console.log('[pickup] late answer kept');
            setPickup((prev) =>
              prev
                ? { ...prev, phase: 'switching', note: null, canCancel: false }
                : { call: current.call, match: current.match, phase: 'switching', note: null },
            );
            routeSwitched(answered);
          })
          .catch(() => {});
      }
      return;
    }
    routeSwitched(session);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adoptClientCall, dismissPickup, navigate]);

  // Cancel on the card: only while the switch is still being prepared (the
  // INVITE has not gone out), so nothing is ever half switched.
  const cancelPickup = useCallback(() => {
    pickupAttemptRef.current = null;
    setPickup((prev) =>
      prev && prev.phase === 'switching' && prev.canCancel ? { ...prev, phase: 'offer', note: null, canCancel: false } : prev,
    );
  }, []);

  const hangUpPickedUp = useCallback(() => {
    // Clear the ref first: the session's ended event fires inside the
    // hang-up, before React re-renders, and must not toast "The call ended."
    pickupRef.current = null;
    setPickup(null);
    try {
      hangupActiveCall();
    } catch (_) {}
    setPendingPickup(null);
    setMuted(false);
  }, []);

  // The one Direct hot transfer to the owner: a blind REFER to their line,
  // then one History line (unless the webhook already noted a hot seat).
  const handOffPickedUpCall = useCallback(async (target) => {
    const current = pickupRef.current;
    if (!current?.call?.sessionId || !target?.extension) return;
    // The REFER completes with a BYE on our leg, which fires the ended
    // listener: mark the hand-off first so it stays quiet.
    pickupRef.current = { ...current, handingOff: true };
    setPickup((prev) => (prev ? { ...prev, handingOff: true } : prev));
    try {
      await transferActiveCall(target.extension);
    } catch (e) {
      pickupRef.current = { ...current, handingOff: false };
      setPickup((prev) => (prev ? { ...prev, handingOff: false } : prev));
      toast.error(PICKUP_COPY.failed);
      return;
    }
    try {
      await axios.post(`/presence/my-call/${encodeURIComponent(current.call.sessionId)}/handed-off`, { toUserId: target.userId });
    } catch (_) {
      /* the transfer went through; the line is best effort */
    }
    toast.success(PICKUP_COPY.handed(String(target.name || '').split(' ')[0] || 'They'));
    pickupRef.current = null;
    setPickup(null);
    setPendingPickup(null);
    setMuted(false);
  }, []);

  // ---- Starting a call -----------------------------------------------------

  // Opening the center does NOT dial (Nic 2026-08-12): the specialist gets
  // a beat to glance at the script and the person, then presses Place call
  // — the same rhythm as the old dialer.
  const startStepCall = useCallback(
    ({ run, step, boardContext = {} }) => {
      const lead = run?.lead;
      if (!lead?.leadPhone || !step) return;
      if (refuseIfNotOwner(run, boardContext)) return;
      if (callRef.current || pickupLive()) {
        toast.error('Finish the current call first.');
        return;
      }
      // Without this, setActiveSession would silently kill a live test call.
      // Status check (not mere existence) so a modal resting on its "ended"
      // screen never blocks real work.
      if (
        testCallRef.current?.status === 'live' ||
        testCallRef.current?.status === 'dialing'
      ) {
        toast.error('Finish the test call first.');
        return;
      }
      setCall({
        run,
        step,
        lead,
        boardContext,
        placed: false,
        dialing: false,
        placedAt: null,
        rcCallSessionId: null,
        callLive: false,
        contactMade: false,
        leadOutreachId: null,
        declaredOutcome: null,
        linkSent: false,
        minimized: false,
        transferStaged: null,
        consult: null,
        failed: false,
      });
      setMuted(false);
    },
    [refuseIfNotOwner],
  );

  // Follow-up call from Contact Made (Nic 2026-08-12): same command center,
  // but contact is already made — no Made-contact gate (the action row shows
  // the moment the line opens) and no outcome quiz on hangup (there is no
  // step to grade; the wrap-up view closes it). The SYNTHETIC step carries
  // the label + shape the card renders from; its null cadenceStepId is what
  // every step-dependent path guards on. The touch is logged server-side at
  // placement (logFollowUpCall).
  // readOnly (Nic 2026-10-23): someone who isn't the owner (the manager,
  // an admin, a viewer) opens the same Workspace to look. Nothing dials from
  // it and every action is greyed; it still takes the one call slot.
  // `adopted` (2026-11-02): the call is ALREADY live in this browser (picked
  // up from the RingCentral app): the Workspace mounts on the live view in
  // one render, no "Place call" first.
  const startFollowUpCall = useCallback(
    ({ run, boardContext = {}, mode = 'guide', tab = null, readOnly = false, adopted = null }) => {
      const lead = run?.lead;
      if (!lead) return;
      if (!readOnly && !lead.leadPhone) return;
      if (!readOnly && refuseIfNotOwner(run, boardContext)) return;
      // An adopted start IS the picked-up call arriving: never refused for it.
      if (callRef.current || (pickupLive() && !adopted)) {
        toast.error('Finish the current call first.');
        return;
      }
      // Without this, setActiveSession would silently kill a live test call.
      // Status check (not mere existence) so a modal resting on its "ended"
      // screen never blocks real work.
      if (
        testCallRef.current?.status === 'live' ||
        testCallRef.current?.status === 'dialing'
      ) {
        toast.error('Finish the test call first.');
        return;
      }
      setCall({
        run,
        step: {
          cadenceStepId: null,
          label: 'Follow-up call',
          stepKey: 'followup',
          type: 'call',
          leaveVoicemail: false,
        },
        lead,
        boardContext,
        followUp: true,
        placed: !!adopted,
        dialing: false,
        placedAt: adopted?.startedAt || null,
        rcCallSessionId: adopted?.telephonySessionId || null,
        callLive: !!adopted,
        adopted: !!adopted,
        contactMade: true,
        leadOutreachId: null,
        declaredOutcome: null,
        linkSent: false,
        minimized: false,
        transferStaged: null,
        consult: null,
        failed: false,
        workspaceMode: !readOnly && mode === 'signup' ? 'signup' : 'guide',
        // The Workspace tab to open on (Nic 2026-10-21: a row's notes button
        // opens Notes, like the Onboarding Workspace).
        openTab: tab,
        readOnly,
      });
      setMuted(false);
      if (adopted) {
        if (adopted.startedAt) {
          setElapsedMs(Math.max(0, Date.now() - new Date(adopted.startedAt).getTime()));
        }
        // The picked-up call has its Workspace now: the card and the
        // pending hand-off are done (cleared here, after the start could
        // have refused, so a refusal never strands the audio).
        pickupRef.current = null;
        setPickup(null);
        setPendingPickup(null);
      }
    },
    [refuseIfNotOwner],
  );

  // Outside-of-cadence call (Nic 2026-08-13): an ad-hoc call to a TAKEN lead
  // in the day columns — a missed call or inbound email deserves an immediate
  // dial that counts toward NO checklist step. Same synthetic-null-step
  // machinery as follow-up, but the run is still ACTIVE: contactMade starts
  // false and Made contact pivots the run normally (marking contact, ticking
  // nothing). The touch + per-lead usage counter log server-side at placement
  // (logOutsideCadenceCall).
  const startOutsideCadenceCall = useCallback(
    ({ run, boardContext = {}, adopted = null }) => {
      const lead = run?.lead;
      if (!lead?.leadPhone) return;
      if (refuseIfNotOwner(run, boardContext)) return;
      // A run that hasn't started yet is locked (QA 2026-10-26): Start now
      // first, the same as the side panel. A picked-up call is already a
      // conversation (2026-11-02): never refused.
      if (
        !adopted &&
        run.state === 'active' &&
        run.startsAt &&
        new Date(run.startsAt).getTime() > Date.now()
      ) {
        toast.error("Their calls haven't started yet. Start now first.");
        return;
      }
      if (callRef.current || (pickupLive() && !adopted)) {
        toast.error('Finish the current call first.');
        return;
      }
      // Without this, setActiveSession would silently kill a live test call.
      // Status check (not mere existence) so a modal resting on its "ended"
      // screen never blocks real work.
      if (
        testCallRef.current?.status === 'live' ||
        testCallRef.current?.status === 'dialing'
      ) {
        toast.error('Finish the test call first.');
        return;
      }
      setCall({
        run,
        step: {
          cadenceStepId: null,
          label: 'Outside the cadence',
          stepKey: 'outside_cadence',
          type: 'call',
          leaveVoicemail: false,
        },
        lead,
        boardContext,
        outsideCadence: true,
        placed: !!adopted,
        dialing: false,
        placedAt: adopted?.startedAt || null,
        rcCallSessionId: adopted?.telephonySessionId || null,
        callLive: !!adopted,
        adopted: !!adopted,
        contactMade: false,
        leadOutreachId: null,
        declaredOutcome: null,
        linkSent: false,
        minimized: false,
        transferStaged: null,
        consult: null,
        failed: false,
      });
      setMuted(false);
      if (adopted) {
        if (adopted.startedAt) {
          setElapsedMs(Math.max(0, Date.now() - new Date(adopted.startedAt).getTime()));
        }
        // The picked-up call has its Workspace now: the card and the
        // pending hand-off are done (cleared here, after the start could
        // have refused, so a refusal never strands the audio).
        pickupRef.current = null;
        setPickup(null);
        setPendingPickup(null);
      }
    },
    [refuseIfNotOwner],
  );

  const placeCall = useCallback(async () => {
    const current = callRef.current;
    if (!current || current.placed || current.dialing) return;
    // A read-only Workspace never dials (Nic 2026-10-23).
    if (current.readOnly) return;
    const { run, step, lead } = current;

    const authorized = await ensureAuthorized();
    if (!authorized) {
      const popupOk = await openAuthorizePopup();
      if (!popupOk) {
        toast.error(
          'Log into RingCentral to place calls. Use the RingCentral chip in the board header to sign in.',
        );
        // A client call has no Place call screen to come back to (QA
        // 2026-10-26): clear it, or every next call says finish this one.
        if (current.client) {
          callRef.current = null;
          setCall(null);
        }
        return;
      }
    }

    const placedAt = new Date().toISOString();
    patchCall({
      placed: true,
      dialing: true,
      callLive: true,
      placedAt,
      failed: false,
    });
    setMuted(false);

    if (current.client) {
      // Nothing to log (Nic 2026-10-12): the call goes through RingCentral,
      // so the client call sync finds it in the call log, with whether it
      // connected and how long it ran.
    } else if (current.followUp) {
      // No step to stamp — record the touch itself (call/outbound on the
      // lead's record). Best effort; the call proceeds regardless.
      logFollowupCallMut.mutate({ cadenceRunId: run.cadenceRunId });
    } else if (current.outsideCadence) {
      // Same no-step logging, PLUS the per-lead usage counter row.
      logOutsideCadenceCallMut.mutate({ cadenceRunId: run.cadenceRunId });
    } else if (run?.cadenceRunId) {
      callStartedMut.mutate({
        cadenceRunId: run.cadenceRunId,
        cadenceStepId: step.cadenceStepId,
      });
    }
    // The server already shows this lead "On call" (callStarted above). A
    // dial that never connects has no hangup to clear it, so the tile sat
    // "On call" until the 15-minute safety TTL (Nic 2026-09-23). Clear it on
    // every failure path.
    const clearOnCall = () => {
      if (!current.followUp && !current.outsideCadence && run?.cadenceRunId) {
        callEndedMut.mutate({ cadenceRunId: run.cadenceRunId, failed: true });
      }
    };

    try {
      setAudioElements(remoteAudioRef.current, localAudioRef.current);
      // No caller ID override: internalUserPhone is whatever number happens to
      // be on the user row, which is NOT reliably that person's RC line
      // (Nic 2026-08-30). Passing nothing lets RC use the extension's own
      // default caller ID, which is always correct by construction.
      const session = await startCallTo(lead.leadPhone);
      if (!session && current.client) {
        resetWebPhone();
        setCall(null);
        return;
      }
      if (!session) {
        // startCallTo toasts its own errors (or redirected to auth). Drop the
        // cached webphone too — a silently dead SIP registration (laptop
        // sleep, network change) fails exactly here, and the reset makes the
        // next Place call re-provision instead of redialing into the void.
        resetWebPhone();
        clearOnCall();
        patchCall({ callLive: false, failed: true, dialing: false });
        return;
      }
      // RC answers the SIP leg the moment the INVITE lands — before the
      // veteran's phone even rings — so pickup is NOT detectable here (the
      // SDK's 'answered' fires immediately and while call() is still
      // awaited). The line is open from now on; Made contact is the human
      // pickup signal.
      patchCall({ dialing: false });
      // RC telephony session id + SIP tags: poll briefly (the headers appear
      // once the call progresses) + last-chance read on disposed. The id
      // tags the step record; the TAGS are the "take back control" recovery
      // key after a refresh (the Replaces header ingredients).
      let fired = false;
      let sipCaptured = false;
      const readId = () => {
        try {
          return session.sessionId || null;
        } catch (_) {
          return null;
        }
      };
      const captureSip = () => {
        if (sipCaptured) return;
        try {
          if (session.sessionId && session.localTag && session.remoteTag) {
            sipCaptured = true;
            sessionStorage.setItem(
              'cc_recovery_sip',
              JSON.stringify({
                telephonySessionId: session.sessionId,
                fromTag: session.localTag,
                toTag: session.remoteTag,
              }),
            );
          }
        } catch (_) {}
      };
      const fire = () => {
        captureSip();
        if (fired) return;
        const id = readId();
        if (id) {
          fired = true;
          patchCall({ rcCallSessionId: id });
        }
      };
      let tries = 0;
      const poll = setInterval(() => {
        tries += 1;
        fire();
        if ((fired && sipCaptured) || tries >= 40) clearInterval(poll);
      }, 500);
      try {
        session.on('disposed', () => {
          clearInterval(poll);
          fire();
        });
      } catch (_) {}
      fire();
    } catch (e) {
      resetWebPhone();
      clearOnCall();
      patchCall({ callLive: false, failed: true, dialing: false });
    }
  }, [
    startCallTo,
    callStartedMut,
    callEndedMut,
    logFollowupCallMut,
    logOutsideCadenceCallMut,
    patchCall,
  ]);

  // A Client Onboarding call (Nic 2026-10-10): dials at once from their
  // Workspace's Call button. client: { onboardingId, firstName, lastName,
  // phone }. No run, step or wrap-up; the Workspace is the context.
  const startClientCall = useCallback(
    (client) => {
      if (!client?.phone || !client?.onboardingId) return;
      if (callRef.current || pickupLive()) {
        toast.error('Finish the current call first.');
        return;
      }
      if (
        testCallRef.current?.status === 'live' ||
        testCallRef.current?.status === 'dialing'
      ) {
        toast.error('Finish the test call first.');
        return;
      }
      const next = {
        client,
        run: null,
        step: null,
        // The dock reads the lead's name and phone.
        lead: {
          leadFirstName: client.firstName || '',
          leadLastName: client.lastName || '',
          leadPhone: client.phone,
        },
        boardContext: {},
        placed: false,
        dialing: false,
        placedAt: null,
        rcCallSessionId: null,
        callLive: false,
        contactMade: false,
        leadOutreachId: null,
        declaredOutcome: null,
        linkSent: false,
        minimized: false,
        transferStaged: null,
        consult: null,
        failed: false,
      };
      callRef.current = next;
      setCall(next);
      setMuted(false);
      placeCall();
    },
    [placeCall],
  );

  // The dock's expand on a client call: back to their Workspace (opening it
  // when it is closed, from any page).
  const restoreClientCall = useCallback(() => {
    const current = callRef.current;
    if (!current?.client) return;
    setCall((prev) => (prev ? { ...prev, minimized: false } : prev));
    navigate(`/operations/client-onboarding?open=${current.client.onboardingId}`);
  }, [navigate]);

  // ---- Test Audio (settings menu) ------------------------------------------

  // Mirrors placeCall's dial sequence minus all run/lead logging: no
  // callStartedMut, no cc_recovery_* writes, no session-id polling. The
  // recovery/orphan/wake-lock effects all key off call?.callLive, which stays
  // null for the whole test, so they are untouched. The server has already
  // vetted the number (POST /test-call/check) before this is called.
  const startTestCall = useCallback(
    async (rawNumber) => {
      if (!rawNumber) return;
      if (
        callRef.current ||
        testCallRef.current?.status === 'live' ||
        testCallRef.current?.status === 'dialing'
      ) {
        toast.error('Finish the current call first.');
        return;
      }

      // Popup auth, never startCallTo's page redirect: a full-page
      // navigation would destroy the modal mid-flow.
      const authorized = await ensureAuthorized();
      if (!authorized) {
        const popupOk = await openAuthorizePopup();
        if (!popupOk) {
          toast.error(
            'Log into RingCentral to place calls. Use the RingCentral chip in the board header to sign in.',
          );
          return;
        }
      }

      // Written to the ref SYNCHRONOUSLY (state catches up on render) so a
      // second call in the same frame — double-Enter — trips the guard.
      testCallRef.current = { number: rawNumber, status: 'dialing' };
      setTestCall(testCallRef.current);
      try {
        setAudioElements(remoteAudioRef.current, localAudioRef.current);
        // No caller ID override (unlike placeCall's internalUserPhone): RC
        // uses the extension default for a self-test.
        const session = await startCallTo(rawNumber);
        if (!session) {
          // Same dead-SIP self-heal as placeCall: reset so the next attempt
          // re-provisions instead of redialing into the void.
          resetWebPhone();
          setTestCall((prev) => (prev ? { ...prev, status: 'failed' } : prev));
          return;
        }
        // Cancelled while the SIP leg was establishing? (Modal closed, End
        // call pressed, or the board unmounted.) The hangup back then was a
        // no-op — no active session existed yet — so kill the session now
        // rather than leave a live untracked call.
        const still = testCallRef.current;
        if (!still || still.status !== 'dialing') {
          try {
            hangupActiveCall();
          } catch (_) {}
          return;
        }
        testCallRef.current = { ...still, status: 'live' };
        setTestCall(testCallRef.current);
      } catch (_) {
        resetWebPhone();
        setTestCall((prev) => (prev ? { ...prev, status: 'failed' } : prev));
      }
    },
    [startCallTo],
  );

  const endTestCall = useCallback(() => {
    const t = testCallRef.current;
    if (t && t.status === 'dialing') {
      // No active session exists yet, so hangupActiveCall alone would no-op
      // and the call would go live anyway. Flip the state now — the
      // post-dial check in startTestCall disposes the session on arrival.
      testCallRef.current = { ...t, status: 'ended' };
      setTestCall(testCallRef.current);
    }
    try {
      hangupActiveCall();
    } catch (_) {}
    // 'terminated' fires from dispose and flips a live status to 'ended'.
  }, []);

  const clearTestCall = useCallback(() => {
    testCallRef.current = null;
    setTestCall(null);
  }, []);

  // ---- In-call controls ----------------------------------------------------

  const toggleMute = useCallback(() => {
    try {
      if (isLocalAudioMuted()) {
        unmuteLocalAudio();
        setMuted(false);
      } else {
        muteLocalAudio();
        setMuted(true);
      }
    } catch (_) {}
  }, []);

  // Call again from the workspace after a call ended (Nic 2026-09-24): same
  // lead, same workspace, a fresh dial. Once contact is made it goes out as
  // a follow-up call (logged as a touch at placement, no step). The ref is
  // set by hand so placeCall sees the reset call in this same tick.
  // Not reached yet (QA 2026-10-26): it stays the same step call or
  // outside-the-cadence call, so it's logged the way the first dial was (a
  // follow-up call needs contact made and was refused, logging nothing).
  const redial = useCallback(() => {
    const current = callRef.current;
    if (!current || current.callLive || current.dialing) return;
    const keepMode = !current.contactMade && !current.client;
    const reset = {
      ...current,
      ...(keepMode
        ? {}
        : {
            step: {
              cadenceStepId: null,
              label: 'Follow-up call',
              stepKey: 'followup',
              type: 'call',
              leaveVoicemail: false,
            },
            followUp: true,
            outsideCadence: false,
          }),
      placed: false,
      dialing: false,
      callLive: false,
      placedAt: null,
      rcCallSessionId: null,
      failed: false,
      transferStaged: null,
      consult: null,
    };
    callRef.current = reset;
    setCall(reset);
    placeCall();
  }, [placeCall]);

  const hangup = useCallback(() => {
    try {
      hangupActiveCall();
    } catch (_) {}
    // 'terminated' fires from dispose and drives the state transition.
  }, []);

  const closeCenter = useCallback(() => {
    const current = callRef.current;
    if (current?.callLive) {
      hangup();
      return;
    }
    // Never placed = free close: nothing happened, nothing to record.
    setCall(null);
  }, [hangup]);

  // ---- Stepped actions -----------------------------------------------------

  // "Made contact" mid-call: record the call step as reached (SLA stamped at
  // placement) then pivot the run to contact_made — the same mutation pair
  // the outcome sheet fires, just at the moment of truth.
  const madeContact = useCallback(async () => {
    const current = callRef.current;
    if (!current || current.contactMade) return;
    // Outside-of-cadence calls have NO step to grade: the pivot to Contact
    // Made is real, the checklist stays untouched.
    let leadOutreachId = null;
    if (current.step?.cadenceStepId) {
      const data = await completeStep.mutateAsync({
        cadenceStepId: current.step.cadenceStepId,
        outcome: 'reached',
        ...(current.placedAt ? { placedAt: current.placedAt } : {}),
        ...(current.rcCallSessionId
          ? { rcCallSessionId: current.rcCallSessionId }
          : {}),
      });
      leadOutreachId = data?.leadOutreachId || null;
    }
    await markContactMade.mutateAsync({
      cadenceRunId: current.run.cadenceRunId,
      via: 'call',
      ...(current.step?.cadenceStepId
        ? { cadenceStepId: current.step.cadenceStepId }
        : {}),
      ...(leadOutreachId ? { leadOutreachId } : {}),
    });
    patchCall({ contactMade: true, leadOutreachId });
    invalidateBoard();
    // The Sales board switches to Signing Up, where they now are.
    window.dispatchEvent(new CustomEvent('callcenter:contact-made'));
  }, [completeStep, markContactMade, patchCall, invalidateBoard]);

  // Post-call outcome question (only when NOTHING was declared). Follow-up
  // calls never reach it (contactMade from birth), but guard anyway — there
  // is no step to grade.
  const recordCallOutcome = useCallback(
    async (outcome) => {
      const current = callRef.current;
      if (!current || !current.step?.cadenceStepId) return null;
      const data = await completeStep.mutateAsync({
        cadenceStepId: current.step.cadenceStepId,
        outcome,
        ...(current.placedAt ? { placedAt: current.placedAt } : {}),
        ...(current.rcCallSessionId
          ? { rcCallSessionId: current.rcCallSessionId }
          : {}),
      });
      invalidateBoard();
      return data;
    },
    [completeStep, invalidateBoard],
  );

  const recordDiscard = useCallback(async () => {
    const current = callRef.current;
    if (!current || !current.step?.cadenceStepId) return;
    await discardCall.mutateAsync({
      cadenceStepId: current.step.cadenceStepId,
      ...(current.placedAt ? { placedAt: current.placedAt } : {}),
      ...(current.rcCallSessionId
        ? { rcCallSessionId: current.rcCallSessionId }
        : {}),
    });
    invalidateBoard();
  }, [discardCall, invalidateBoard]);

  const contactMadeFromSheet = useCallback(
    async (leadOutreachId) => {
      const current = callRef.current;
      if (!current) return;
      await markContactMade.mutateAsync({
        cadenceRunId: current.run.cadenceRunId,
        via: 'call',
        cadenceStepId: current.step.cadenceStepId,
        ...(leadOutreachId ? { leadOutreachId } : {}),
      });
      patchCall({ contactMade: true, leadOutreachId: leadOutreachId || null });
      invalidateBoard();
      window.dispatchEvent(new CustomEvent('callcenter:contact-made'));
    },
    [markContactMade, patchCall, invalidateBoard],
  );

  const contactObserved = useCallback(
    (leadOutreachId) => {
      const current = callRef.current;
      if (!current) return;
      observeContact.mutate({
        cadenceRunId: current.run.cadenceRunId,
        ...(leadOutreachId ? { leadOutreachId } : {}),
      });
    },
    [observeContact],
  );

  // Run outcomes from the action set (post-contact).
  const declareOutcome = useCallback(
    async (outcome) => {
      const current = callRef.current;
      if (!current) return;
      await setRunOutcome.mutateAsync({
        cadenceRunId: current.run.cadenceRunId,
        outcome,
      });
      patchCall({ declaredOutcome: outcome });
      invalidateBoard();
      toast.success(
        outcome === 'booked'
          ? 'Marked booked. Nice work.'
          : outcome === 'link_sent'
            ? 'Moved to Booking link sent. They stay on Signing Up until they book.'
            : 'Moved to Long Term Nurture.',
      );
      // An outcome declared on a dead call closes the panel: the person just
      // answered the question, and falling through would re-ask it. Live
      // calls keep the panel — hanging up is what ends it.
      if (!callRef.current?.callLive) setCall(null);
    },
    [setRunOutcome, patchCall, invalidateBoard],
  );

  // NOTHING LEAVES THE BOARD WITHOUT A HUMAN SAYING SO (Nic 2026-08-15).
  //
  // A detected booking used to CONFIRM ITSELF here, which ends the run as
  // 'booked' and pulls the lead off the board — while the specialist was
  // still on the phone. The premise was "they just booked it themselves in
  // the Calendly tab, so confirming is a pointless click" (2026-08-12), and
  // it does not hold: the booking is found by a 10-second poll, not by the
  // specialist noticing, and people routinely keep talking for minutes after
  // booking. It deleted their working context to save a click they would
  // happily make afterwards.
  //
  // Detection still surfaces mid-call (the Calendly disc greys to "Booked!").
  // Since 2026-09-23 a booking no longer takes the lead off the board at all:
  // the server stages it Meeting booked (or leaves it in Signing up) and the
  // rep keeps it, so there is nothing to confirm (the confirm card and tile
  // pill were removed 2026-09-24).
  //
  // Live-call transfers joined the same rule on 2026-08-15: a landed
  // transfer is OBSERVED (contact stamps, the target persists on the run)
  // and parks in Contact Made until the tile's confirm. There are now NO
  // automatic off-board moves at all, bar the day-11 expiry sweep — which
  // only touches ACTIVE runs, so a lead waiting on a confirmation (booking
  // or transfer) cannot age out.

  // ---- Transfer (blind + warm, lifted from RcCallModal) --------------------

  const consultTokenRef = useRef(null);
  // The warm consult (2026-10-29): its ring timer, and the SDK's complete
  // handle once the consult leg is up.
  const ringTimerRef = useRef(null);
  const consultHandleRef = useRef(null);

  // The only transfer is the hot seat (Nic 2026-10-21): once they have
  // signed, a transfer hands the CLIENT to that Onboarding Specialist.
  // Before that there is no transfer at all; the lead stays on the rep's
  // Signing Up. transferStaged lets the card show the closing confirmation;
  // declaredOutcome 'transferred' marks the hot seat as recorded.
  // How it went is RingCentral's to say (Nic 2026-10-23): this only notes
  // who it went to. warm = the specialist was on the consult first, so that
  // one is connected.
  const settleTransfer = useCallback(
    async (target, { warm = false, mode = null, redirectFrom = null, redirectFromName = null, reason = null } = {}) => {
      const current = callRef.current;
      if (!current) return;
      // Hot seat (Nic 2026-09-28): once they have signed, the run is over
      // and a transfer hands the CLIENT to that Onboarding Specialist.
      if (current.hotSeatUserId) {
        // Already recorded (a retry, or the footer prompt): never twice.
        if (current.hotSeatDone) {
          patchCall({ declaredOutcome: 'transferred' });
          return;
        }
        try {
          await recordHotSeat.mutateAsync({
            userId: current.hotSeatUserId,
            toUserId: target.userId,
            warm,
            mode: mode || (warm ? 'warm' : 'direct'),
            // A redirect (2026-10-29): who it was meant for, and why not them.
            redirectFrom,
            reason,
          });
          // declaredOutcome closes the post-transfer card cleanly (QA
          // 2026-09-28: without it the rep sat on "recording it failed").
          patchCall({
            declaredOutcome: 'transferred',
            hotSeatDone: { toName: target.name, insteadOf: redirectFrom ? redirectFromName : null },
          });
          toast.success(`${target.name} has them now.`);
        } catch (e) {
          toast.error(
            e?.response?.data?.msg ||
              'The transfer went through, but saving it failed. Try Close it out.',
          );
        }
        return;
      }
      // No transfer before they sign (Nic 2026-10-21): the old one ended the
      // run with nobody's board to land on. The Transfer button only shows
      // once they've signed, so this is a safety net: keep the lead, say so.
      patchCall({ transferStaged: null });
      toast.error(
        'Transfers are for after they sign. They stay on your Signing Up.',
      );
    },
    [recordHotSeat, patchCall],
  );

  // The workspace names the signed client, so a transfer becomes a hot seat.
  const setHotSeatClient = useCallback(
    (userId) => {
      if (callRef.current && callRef.current.hotSeatUserId !== userId) {
        patchCall({ hotSeatUserId: userId });
      }
    },
    [patchCall],
  );
  const markHotSeatDone = useCallback(
    (done) => patchCall({ hotSeatDone: done }),
    [patchCall],
  );
  // An edit made in the Workspace (the email pencil, 2026-10-21): the call
  // holds its own copy of the lead, so it takes the change too.
  const patchLead = useCallback(
    (patch) =>
      setCall((prev) =>
        prev ? { ...prev, lead: { ...prev.lead, ...patch } } : prev,
      ),
    [],
  );

  // No transfer before they sign (Nic 2026-10-21). The button only shows
  // once signed; this keeps the call where it is if it ever shows early.
  const notSignedYet = () => {
    if (callRef.current?.hotSeatUserId) return false;
    toast.error('Transfers are for after they sign. They stay on your Signing Up.');
    return true;
  };

  // Direct (2026-10-29: the rep's choice each time): the REFER, then the
  // record. meta carries a redirect (redirectFrom, reason) when the rep
  // picked someone else because their specialist wasn't free.
  const beginBlindTransfer = useCallback(
    async (target, meta = {}) => {
      const current = callRef.current;
      if (!current) return;
      if (notSignedYet()) return;
      try {
        // Stage the confirm BEFORE the REFER: our leg terminates the instant
        // the transfer lands, and the context must already be there.
        patchCall({ transferStaged: { target, mode: 'blind' } });
        await transferActiveCall(target.extension);
        await settleTransfer(target, { mode: 'direct', ...meta });
      } catch (e) {
        patchCall({ transferStaged: null });
        toast.error(e?.message || 'Transfer failed');
      }
    },
    [patchCall, settleTransfer],
  );

  // Warm (2026-10-29): the client goes on hold, the specialist's line
  // rings, and only once they are on does the rep hand over.
  //
  // call.consult = { target, state, since, meta, wasMuted }, states:
  //   holding    putting the client on hold
  //   dialing    the consult is being placed
  //   ringing    the specialist's line rings (RingCentral answers the SIP
  //              leg at once, so this is NOT "they picked up")
  //   talking    they picked up: the server saw an Answered frame on their
  //              line (CallWorkspace polls it), or the rep heard them
  //   completing the REFER with Replaces is out
  //   no_answer  nobody picked up (the server said missed or voicemail, the
  //              consult dropped, the rep hit Cancel, or 20 seconds passed):
  //              the consult is hung up and the client is back
  // Every way back to the client = hang up the consult, then unhold, as two
  // guarded steps (session.js returnToClient). The SDK's own cancel() is
  // never used: a BYE that fails would skip the unhold.
  const endConsult = useCallback(
    async (state, { say = null } = {}) => {
      const current = callRef.current;
      const consult = current?.consult;
      consultTokenRef.current = null;
      if (ringTimerRef.current) {
        clearTimeout(ringTimerRef.current);
        ringTimerRef.current = null;
      }
      await returnToClient({ wasMuted: !!consult?.wasMuted }).catch(() => {});
      patchCall({ consult: state === 'no_answer' && consult ? { ...consult, state: 'no_answer' } : null });
      if (say) toast.info(say);
    },
    [patchCall],
  );

  const beginWarmTransfer = useCallback(
    async (target, meta = {}) => {
      if (notSignedYet()) return;
      if (callRef.current?.consult && callRef.current.consult.state !== 'no_answer') return;
      const token = {};
      consultTokenRef.current = token;
      const wasMuted = isLocalAudioMuted();
      const first = String(target.name || '').split(' ')[0] || 'them';
      patchCall({ consult: { target, state: 'holding', since: Date.now(), meta, wasMuted } });
      let consultSession = null;
      try {
        const started = await warmTransferActiveCall(target.extension);
        consultSession = started.consultSession;
        if (consultTokenRef.current !== token) {
          // Cancelled while dialing: straight back.
          await endConsult(null);
          return;
        }
        patchCall({ consult: { target, state: 'dialing', since: Date.now(), meta, wasMuted } });
        const onGone = () => {
          if (consultTokenRef.current !== token) return;
          const now = callRef.current?.consult;
          if (!now || now.state === 'completing') return;
          endConsult('no_answer', { say: now.state === 'talking' ? `${first} hung up. Back to the caller.` : `${first} didn't pick up. Back to the caller.` });
        };
        try {
          consultSession.on('disposed', onGone);
          consultSession.on('failed', onGone);
        } catch (_) {}
        // The SIP answer (at once, by RingCentral's design): now it rings.
        const answered = await started.result
          .then((handle) => {
            consultHandleRef.current = handle;
            return consultSession?.state === 'answered';
          })
          .catch(() => false);
        if (consultTokenRef.current !== token) return;
        if (!answered) {
          await endConsult('no_answer', { say: `${first} didn't pick up. Back to the caller.` });
          return;
        }
        patchCall({ consult: { target, state: 'ringing', since: Date.now(), meta, wasMuted } });
        ringTimerRef.current = setTimeout(() => {
          if (consultTokenRef.current !== token) return;
          if (callRef.current?.consult?.state !== 'ringing') return;
          endConsult('no_answer', { say: `${first} didn't pick up. Back to the caller.` });
        }, CONSULT_RING_MS);
      } catch (e) {
        await endConsult(null);
        toast.error(e?.message || 'Could not start the transfer');
      }
    },
    [patchCall, endConsult],
  );

  // They picked up: the server saw their line answer, or the rep heard it.
  const consultAnswered = useCallback(() => {
    const consult = callRef.current?.consult;
    if (!consult || consult.state !== 'ringing') return;
    if (ringTimerRef.current) {
      clearTimeout(ringTimerRef.current);
      ringTimerRef.current = null;
    }
    patchCall({ consult: { ...consult, state: 'talking', since: Date.now() } });
  }, [patchCall]);

  // The server saw their line miss it (no answer, voicemail).
  const consultMissed = useCallback(() => {
    const consult = callRef.current?.consult;
    if (!consult || consult.state !== 'ringing') return;
    const first = String(consult.target?.name || '').split(' ')[0] || 'They';
    endConsult('no_answer', { say: `${first} didn't pick up. Back to the caller.` });
  }, [endConsult]);

  const completeWarmTransfer = useCallback(async () => {
    const current = callRef.current;
    const consult = current?.consult;
    if (!consult || consult.state !== 'talking') return;
    const { target, meta } = consult;
    try {
      patchCall({
        transferStaged: { target, mode: 'warm' },
        consult: { ...consult, state: 'completing', since: Date.now() },
      });
      // Both legs join; our session disposes -> terminated handler runs.
      // "Complete transfer" WAS the confirmation: the specialist was on the
      // consult, so this one is connected.
      const handle = consultHandleRef.current;
      if (!handle) throw new Error('The consult is gone.');
      await handle.complete();
      consultTokenRef.current = null;
      patchCall({ consult: null });
      await settleTransfer(target, { warm: true, mode: 'warm', ...(meta || {}) });
    } catch (e) {
      patchCall({ transferStaged: null, consult: { ...consult, state: 'talking' } });
      toast.error(e?.message || 'Could not complete the transfer');
    }
  }, [patchCall, settleTransfer]);

  // Cancel while it rings = nobody picked up (the rep heard voicemail, or
  // gave up): the pick list opens. Back from a talking consult = idle.
  const cancelWarmTransfer = useCallback(async () => {
    const consult = callRef.current?.consult;
    if (!consult) return;
    if (consult.state === 'completing') return;
    await endConsult(consult.state === 'talking' || consult.state === 'no_answer' ? null : 'no_answer');
  }, [endConsult]);

  // Manual retry for the RARE case where the call transferred but the
  // observe write failed (a landed transfer records itself on success).
  // Closes the center when it lands.
  const confirmTransferOutcome = useCallback(async () => {
    const current = callRef.current;
    if (!current?.transferStaged) return;
    await settleTransfer(current.transferStaged.target, { warm: current.transferStaged.mode === 'warm' });
    if (callRef.current?.declaredOutcome === 'transferred') setCall(null);
  }, [settleTransfer]);

  const minimize = useCallback(
    () => patchCall({ minimized: true }),
    [patchCall],
  );
  // The call workspace's center (Nic 2026-09-23): 'guide' (talk track) or
  // 'signup' (the signup steps inline).
  const setWorkspaceMode = useCallback(
    (mode) =>
      patchCall({
        workspaceMode: mode === 'signup' ? 'signup' : 'guide',
        minimized: false,
        // The tab asked for at open is used once (a row's notes button).
        openTab: null,
      }),
    [patchCall],
  );
  const restore = useCallback(
    () => patchCall({ minimized: false }),
    [patchCall],
  );

  // Open the signup for a lead. From a live call the center docks (the
  // flyover shows the call); with no call it simply opens.
  const openSignup = useCallback(
    (target) => {
      const current = callRef.current;
      const leadId = target?.leadId || current?.lead?.leadId;
      if (!leadId) return;
      // Someone else's lead is theirs to sign up (a run with an owner).
      if (target?.run && refuseIfNotOwner(target.run, target.boardContext)) {
        return;
      }
      // Once contact is made on THIS lead's call, signup happens inside the
      // call workspace (Nic 2026-09-23), beside the person and the notes.
      if (current && current.contactMade && current.lead?.leadId === leadId) {
        patchCall({ workspaceMode: 'signup', minimized: false });
        return;
      }
      // No call yet: open the call workspace on its Sign up tab (Nic
      // 2026-09-24), the one place for calls and signing up. Place call
      // sits at the top. The bare flyover stays only as the fallback for
      // a lead without a run/phone or while another call is live.
      if (!current && target?.run?.lead?.leadPhone) {
        startFollowUpCall({
          run: target.run,
          boardContext: target.boardContext || {},
          mode: 'signup',
        });
        return;
      }
      const lead = target?.lead || current?.lead || {};
      const ownerRun = target?.run || current?.run || null;
      setSignup({
        leadId,
        ownerUserId: ownerRun ? ownerRun.ownerUserId || null : undefined,
        leadName:
          target?.leadName ||
          `${lead.leadFirstName || ''} ${lead.leadLastName || ''}`.trim(),
      });
      if (current) patchCall({ minimized: true });
    },
    [patchCall, startFollowUpCall, refuseIfNotOwner],
  );
  const closeSignup = useCallback(() => {
    setSignup(null);
    // Back to the call (live controls or its wrap-up).
    if (callRef.current) patchCall({ minimized: false });
  }, [patchCall]);

  // A reload or closed tab drops a browser call: warn while one is live.
  useEffect(() => {
    if (!call?.callLive) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [call?.callLive]);

  const actions = useMemo(
    () => ({
      startStepCall,
      placeCall,
      redial,
      hangup,
      closeCenter,
      toggleMute,
      minimize,
      restore,
      openSignup,
      setWorkspaceMode,
      madeContact,
      recordCallOutcome,
      recordDiscard,
      contactMadeFromSheet,
      contactObserved,
      declareOutcome,
      beginBlindTransfer,
      beginWarmTransfer,
      completeWarmTransfer,
      cancelWarmTransfer,
      consultAnswered,
      consultMissed,
      confirmTransferOutcome,
      setHotSeatClient,
      markHotSeatDone,
      patchLead,
    }),
    [
      setHotSeatClient,
      markHotSeatDone,
      patchLead,
      openSignup,
      setWorkspaceMode,
      startStepCall,
      placeCall,
      redial,
      hangup,
      closeCenter,
      toggleMute,
      minimize,
      restore,
      madeContact,
      recordCallOutcome,
      recordDiscard,
      contactMadeFromSheet,
      contactObserved,
      declareOutcome,
      beginBlindTransfer,
      beginWarmTransfer,
      completeWarmTransfer,
      cancelWarmTransfer,
      consultAnswered,
      consultMissed,
      confirmTransferOutcome,
    ],
  );

  const ctxValue = useMemo(
    () => ({
      active: call,
      dockVisible: !!call && call.minimized,
      startStepCall,
      startFollowUpCall,
      startOutsideCadenceCall,
      startClientCall,
      setClientPanel,
      // A call picked up from the RingCentral app, waiting for the Sales
      // board to open its Workspace (2026-11-02).
      pendingPickup,
      takePendingPickup,
      // The live pill on a Client Workspace.
      callControls: { muted, elapsedMs, hangup, toggleMute, minimize },
      openSignup,
      signupOpen: !!signup,
      testCall,
      startTestCall,
      endTestCall,
      clearTestCall,
      // A call live in this browser, a pick-up in flight, or a test call:
      // the Connections chip's Sign out waits (it drops the phone).
      busy:
        !!call ||
        !!(pickup && pickup.phase !== 'offer') ||
        testCall?.status === 'live' ||
        testCall?.status === 'dialing',
    }),
    [
      call,
      pickup,
      openSignup,
      signup,
      startStepCall,
      startFollowUpCall,
      startOutsideCadenceCall,
      startClientCall,
      pendingPickup,
      takePendingPickup,
      muted,
      elapsedMs,
      hangup,
      toggleMute,
      minimize,
      testCall,
      startTestCall,
      endTestCall,
      clearTestCall,
    ],
  );

  return (
    <CallCenterContext.Provider value={ctxValue}>
      {children}
      {/* Persistent audio sink: navigation must never unmount the call's
          speaker/mic elements (setAudioElements is module-global). */}
      <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
      <audio ref={localAudioRef} autoPlay muted style={{ display: 'none' }} />
      {call && !call.minimized && !call.client ? (
        <CallCommandCenter
          call={call}
          user={user}
          muted={muted}
          elapsedMs={elapsedMs}
          busy={busy}
          actions={actions}
        />
      ) : null}
      {signup ? (
        <SignupFlyover
          leadId={signup.leadId}
          leadName={signup.leadName}
          ownerUserId={signup.ownerUserId}
          call={call && call.lead?.leadId === signup.leadId ? call : null}
          muted={muted}
          elapsedMs={elapsedMs}
          onToggleMute={toggleMute}
          onHangup={hangup}
          onClose={closeSignup}
        />
      ) : null}
      {/* The flyover's header carries this lead's call; a call with someone
          else keeps its dock. */}
      {call &&
      (call.client
        ? call.minimized || clientPanel !== call.client.onboardingId
        : call.minimized && !(signup && call.lead?.leadId === signup.leadId)) ? (
        <CallDock
          call={call}
          muted={muted}
          elapsedMs={elapsedMs}
          onToggleMute={toggleMute}
          onRestore={call.client ? restoreClientCall : restore}
          onHangup={hangup}
        />
      ) : null}
      {/* Suppressed during a test call: the banner's take-back-control
          would setActiveSession over the live test session and silently
          kill it. The banner returns the moment the test ends. */}
      {pickup && !call && !orphanCall?.recovery ? (
        <PickupCard
          pickup={pickup}
          muted={muted}
          onPickUp={pickUpCall}
          onDismiss={dismissPickup}
          onCancel={cancelPickup}
          onToggleMute={toggleMute}
          onHangUp={hangUpPickedUp}
          onTransfer={handOffPickedUpCall}
        />
      ) : null}
      {orphanCall &&
      !call &&
      !pickup &&
      testCall?.status !== 'live' &&
      testCall?.status !== 'dialing' ? (
        <div className="fixed bottom-4 right-4 z-[1090] flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 shadow-lg">
          <div className="text-xs text-amber-800">
            <span className="font-semibold">
              {orphanCall.isTest
                ? 'Your test call is still connected'
                : 'A call is still connected'}
            </span>
            <span className="block">
              {orphanCall.isTest
                ? 'The refresh cut this page off from it. Hang up from your phone.'
                : 'This page lost its controls (refresh during a call).'}
            </span>
          </div>
          {orphanCall.recovery ? (
            <button
              type="button"
              onClick={takeBackControl}
              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500"
            >
              Take back control
            </button>
          ) : null}
          <button
            type="button"
            onClick={hangupOrphan}
            className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500"
          >
            Hang up
          </button>
          <button
            type="button"
            onClick={() => {
              try {
                sessionStorage.setItem(
                  'cc_orphan_dismissed',
                  orphanCall.telephonySessionId,
                );
              } catch (_) {}
              setOrphanCall(null);
            }}
            className="rounded-lg bg-white px-2 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100"
          >
            {orphanCall.isTest ? 'Dismiss' : 'Keep it'}
          </button>
        </div>
      ) : null}
    </CallCenterContext.Provider>
  );
};

export default CallCenterProvider;
